import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { FunctioningAssessment, CaseGoal } from "@visitproof/types";
import { clean } from "../utils/utils";

// ============================================================================
// ASSESSMENTS
// ============================================================================

const getAssessmentsRef = (caseId: string) =>
  collection(db, "cases", caseId, "assessments");

export const AssessmentService = {
  async getAssessments(caseId: string): Promise<FunctioningAssessment[]> {
    const q = query(getAssessmentsRef(caseId), orderBy("assessmentDate", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      ...(doc.data() as FunctioningAssessment),
      id: doc.id,
    }));
  },

  async createAssessment(
    caseId: string,
    data: Omit<FunctioningAssessment, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    const docRef = await addDoc(getAssessmentsRef(caseId), {
      ...clean(data),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  },

  async updateAssessment(
    caseId: string,
    assessmentId: string,
    data: Partial<FunctioningAssessment>
  ): Promise<void> {
    const docRef = doc(db, "cases", caseId, "assessments", assessmentId);
    await updateDoc(docRef, {
      ...clean(data),
      updatedAt: Timestamp.now(),
    });
  },

  async deleteAssessment(caseId: string, assessmentId: string): Promise<void> {
    const docRef = doc(db, "cases", caseId, "assessments", assessmentId);
    await deleteDoc(docRef);
  },
};

// ============================================================================
// GOALS
// ============================================================================

const getGoalsRef = (caseId: string) =>
  collection(db, "cases", caseId, "goals");

export const GoalService = {
  async getGoals(caseId: string): Promise<CaseGoal[]> {
    const q = query(getGoalsRef(caseId), orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    return snapshot.docs.map((doc) => ({
      ...(doc.data() as CaseGoal),
      id: doc.id,
    }));
  },

  async createGoal(
    caseId: string,
    data: Omit<CaseGoal, "id" | "createdAt" | "updatedAt">
  ): Promise<string> {
    const docRef = await addDoc(getGoalsRef(caseId), {
      ...clean(data),
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  },

  async updateGoal(
    caseId: string,
    goalId: string,
    data: Partial<CaseGoal>
  ): Promise<void> {
    const docRef = doc(db, "cases", caseId, "goals", goalId);
    await updateDoc(docRef, {
      ...clean(data),
      updatedAt: Timestamp.now(),
    });
  },

  async deleteGoal(caseId: string, goalId: string): Promise<void> {
    const docRef = doc(db, "cases", caseId, "goals", goalId);
    await deleteDoc(docRef);
  },
};
